/*********************************************************************
 * © Copyright IBM Corp. 2022
 * Copyright © 2022 Randori https://randori.com - All Rights Reserved.
 *********************************************************************/
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import * as FWT from '@grafana/faro-web-tracing'
import { isNotNil } from '@randori/rootkit'
import debug from 'debug'

// ---------------------------------------------------------------------------

type AllNonNullable<T> = {
  [P in keyof T]-?: NonNullable<T[P]>
}

type TracerConfig = {
  __AGENT_ENDPOINT__?: string | null
  __NAME__?: string
  __TRACES__?: boolean
  __VERSION__?: string
}

const hasAllRequiredConfig = (config: TracerConfig): config is AllNonNullable<TracerConfig> => {
  if (!isNotNil(config.__AGENT_ENDPOINT__)) {
    return false
  }

  if (!isNotNil(config.__NAME__)) {
    return false
  }

  if (!isNotNil(config.__VERSION__)) {
    return false
  }

  if (!isNotNil(config.__TRACES__)) {
    return false
  }

  return true
}

class _Tracer {
  #isInitialized = false
  #faro: ReturnType<typeof initializeFaro> | null = null
  #log = debug('RANDORI:tracer')
  #config: TracerConfig

  static #instance: _Tracer

  private constructor(config: TracerConfig) {
    this.#config = config

    if (hasAllRequiredConfig(config) && config.__TRACES__) {
      // @see: https://github.com/grafana/faro-web-sdk/blob/main/packages/core/README.md#options
      this.#faro = initializeFaro({
        url: `${window.location.origin}${config.__AGENT_ENDPOINT__}`,
        instrumentations: [...getWebInstrumentations({ captureConsole: true }), new FWT.TracingInstrumentation()],

        dedupe: true,

        app: {
          name: config.__NAME__,
          version: config.__VERSION__,
        },

        batching: {
          enabled: true,
          sendTimeout: 1000,
          itemLimit: 50,
        },
      })

      this.#isInitialized = true
      this.#log('Tracing initialized', config)

      // pause until authenticated - we need a valid JWT right now
      this.#faro.pause()
    } else {
      this.#log('Tracing noop', config)
    }
  }

  public static get Instance(): _Tracer {
    return (
      this.#instance ??
      (this.#instance = new this({
        __AGENT_ENDPOINT__: window.__RANDORI__.TELEMETRY_ENDPOINT,
        __NAME__: 'web-client',
        __TRACES__: window.__RANDORI__.TRACES ?? false,
        __VERSION__: process.env.__RRELEASE__,
      }))
    )
  }

  public getTracer = () => {
    if (this.#isInitialized && isNotNil(this.#faro)) {
      return this.#faro
    } else {
      this.#log('Tracer was not initialized due incomplete configuration', this.#config)
    }
  }
}

export const Tracer = _Tracer.Instance
